import { createFileRoute, Link } from "@tanstack/react-router";
import { CtaLink } from "@/components/marketing/CtaLink";
import { ArrowRight, Sparkles, Globe, Heart, Shield, Eye, Zap } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroAsset from "@/assets/about/hero.webp.asset.json";
import storyAsset from "@/assets/about/story.webp.asset.json";

const heroImg = heroAsset.url;
const storyImg = storyAsset.url;
const heroAlt =
  "Khách hàng Banana mỉm cười trong bộ vest lịch lãm, cầm thẻ danh thiếp NFC màu tím tại văn phòng cao tầng hiện đại nhìn ra toàn cảnh thành phố";
const storyAlt =
  "So sánh danh thiếp giấy Six Studio đã cũ và hồ sơ Banana kỹ thuật số trên iPhone cùng thẻ NFC thông minh màu tím";

export const Route = createFileRoute("/vi/about")({
  head: () => ({
    meta: [
      { title: "Giới thiệu — banana.cards" },
      { name: "description", content: "Banana là nền tảng liên hệ AI giúp hơn 200.000 chuyên gia biến cuộc trò chuyện thành mối quan hệ bền vững. Khám phá câu chuyện và đội ngũ của chúng tôi." },
      { property: "og:title", content: "Giới thiệu banana.cards" },
      { property: "og:description", content: "Câu chuyện, sứ mệnh và những con người đứng sau banana.cards." },
      { property: "og:image", content: heroImg },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/about", "vi"),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "Giới thiệu banana.cards",
          description: "Banana là nền tảng liên hệ AI giúp hơn 200.000 chuyên gia biến cuộc trò chuyện thành mối quan hệ bền vững.",
          url: "https://banana.cards/vi/about",
          mainEntity: {
            "@type": "Organization",
            name: "Banana Cards",
            url: "https://banana.cards",
            foundingDate: "2022",
            parentOrganization: { "@type": "Organization", name: "SIX STUDIO CREATIVE - FZCO" },
          },
        }),
      },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <SiteLayout>
      <section className="bg-background">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-24 md:grid-cols-2 md:px-6">
          <div className="flex flex-col justify-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Giới thiệu</p>
            <h1 className="mt-3 font-display text-3xl leading-[1.1] md:text-5xl">
              Chúng tôi giúp con người <em className="italic">ghi nhớ con người.</em>
            </h1>
            <p className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
              Banana bắt đầu với một ý tưởng đơn giản: kết nối không nên dừng lại ở câu "rất vui được gặp". Chúng tôi đang xây dựng nền tảng hỗ trợ bởi AI biến cuộc trò chuyện thành mối quan hệ bền vững — và biến mối quan hệ thành tăng trưởng, giao dịch và tình bạn thực tế.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaLink
                to="/signup"
                className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground transition hover:scale-[1.02]"
              >
                Dùng thử Banana miễn phí <ArrowRight className="h-4 w-4" />
              </CtaLink>
              <Link
                to="/vi/contact"
                className="inline-flex items-center gap-2 rounded-xl bg-muted px-6 py-3 text-sm font-bold transition hover:bg-muted/70"
              >
                Trao đổi với đội ngũ
              </Link>
            </div>
          </div>
          <img src={heroImg} alt={heroAlt} className="w-full rounded-2xl object-cover" />
        </div>
      </section>

      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="grid gap-4 md:grid-cols-4">
            {[
              { v: "2022", l: "Thành lập" },
              { v: "30+", l: "Đội ngũ trên 9 quốc gia" },
              { v: "200k+", l: "Chuyên gia trên Banana" },
              { v: "12M+", l: "Kết nối được tạo" },
            ].map((s) => (
              <div key={s.l} className="rounded-2xl bg-card p-8">
                <p className="font-display text-5xl leading-none">{s.v}</p>
                <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-24 md:grid-cols-2 md:px-6">
          <img src={storyImg} alt={storyAlt} className="w-full rounded-2xl object-cover" />
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Câu chuyện của chúng tôi</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              Từ tấm danh thiếp giấy trong ngăn kéo, đến <em className="italic">bộ não trong túi bạn.</em>
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground">
              <p>
                Banana được thành lập năm 2022 sau khi đội ngũ của chúng tôi liên tục đánh mất dấu vết những người gặp tại hội nghị, cuộc họp đầu tư và cuộc gọi khách hàng. Danh thiếp giấy nằm trong ngăn kéo. LinkedIn là nghĩa địa của những người không nhớ. CRM thì trống rỗng.
              </p>
              <p>
                Chúng tôi bắt đầu với một sản phẩm — tấm thẻ NFC được thiết kế đẹp mắt mở hồ sơ kỹ thuật số chỉ trong một lần chạm. Mọi người yêu thích nó. Nhưng phép màu thực sự xảy ra khi chúng tôi thêm AI: ghi chú cuộc họp tự viết, theo dõi được soạn bằng giọng của bạn, và nhắc nhở biết khi nào một liên hệ thực sự quan trọng trở lại.
              </p>
              <p>
                Ngày nay, Banana được hơn 200.000 chuyên gia sử dụng — từ nhà sáng lập đơn lẻ đến đội bán hàng doanh nghiệp — tại hơn 80 quốc gia. Chúng tôi vẫn là một đội nhỏ, vẫn tự thân vận động và vẫn ám ảnh với cùng một câu hỏi: làm thế nào để giúp con người giỏi hơn trong việc làm người?
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-muted">
        <div className="mx-auto max-w-5xl px-4 py-24 md:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Dòng thời gian</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            Bốn năm, <em className="italic">rất nhiều thẻ.</em>
          </h2>
          <ol className="mt-10 grid gap-4 md:grid-cols-2">
            {[
              { y: "2022", t: "Thành lập", d: "Ba nhà đồng sáng lập, một thẻ nguyên mẫu và một Google Sheet người dùng beta." },
              { y: "2023", t: "10.000 khách hàng đầu tiên", d: "Ra mắt phiên bản kim loại, tre và vàng. AI ghi chú phát hành trong Q4." },
              { y: "2024", t: "Kỷ nguyên CRM", d: "Pipeline đầy đủ, hợp đồng, đặt lịch và tự động hóa — một nền tảng, một mức giá." },
              { y: "2025", t: "Doanh nghiệp & toàn cầu", d: "SOC 2 Type II, SAML SSO, lưu trữ dữ liệu tại EU/US/APAC. Hơn 200k người dùng." },
            ].map((m) => (
              <li key={m.y} className="rounded-2xl bg-card p-6">
                <p className="font-display text-3xl">{m.y}</p>
                <h3 className="mt-2 text-lg font-bold">{m.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{m.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 py-24 md:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Nguyên tắc</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            Những gì chúng tôi tin — <em className="italic">viết ra, có chủ đích.</em>
          </h2>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {[
              { i: Heart, t: "Con người trước, luôn luôn.", d: "Phần mềm chỉ hữu ích khi giúp con người làm người tốt hơn. Chúng tôi tối ưu cho cuộc trò chuyện, không phải cú nhấp chuột." },
              { i: Sparkles, t: "AI bổ trợ, không thay thế.", d: "Chúng tôi dùng AI để loại bỏ việc vặt, không để giả vờ ấm áp hay đi đường tắt trong mối quan hệ. Bạn luôn nghe như chính mình." },
              { i: Shield, t: "Quyền riêng tư mặc định.", d: "Liên hệ của bạn thuộc về bạn. Chúng tôi không bao giờ huấn luyện mô hình trên dữ liệu của bạn và không bán cho ai. Xuất bất cứ lúc nào." },
              { i: Eye, t: "Đẹp là tiêu chuẩn.", d: "Công cụ bạn dùng hàng ngày phải là niềm vui khi nhìn vào. Mỗi pixel, mỗi thẻ, mỗi email — được thiết kế có chủ đích." },
              { i: Zap, t: "Tốc độ là một tính năng.", d: "Chúng tôi ám ảnh với độ trễ, hoạt ảnh và thời gian-đến-chạm-đầu-tiên. Công cụ nhanh nhất chiếm thói quen của bạn." },
              { i: Globe, t: "Xây cho cả thế giới.", d: "Thẻ được giao đến hơn 80 quốc gia. AI nói hơn 50 ngôn ngữ. Dữ liệu nằm ở nơi luật pháp của bạn yêu cầu." },
            ].map((p) => {
              const Icon = p.i;
              return (
                <div key={p.t} className="rounded-2xl bg-muted p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-soft">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-4 font-display text-2xl">{p.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Đội ngũ</p>
          <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
            30 người, <em className="italic">9 múi giờ, một nỗi ám ảnh.</em>
          </h2>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground">
            Chúng tôi là một đội làm việc từ xa hoàn toàn — gồm nhà thiết kế, kỹ sư, nghiên cứu viên AI và vận hành đã từng phát hành phần mềm tại Apple, Stripe, Notion, Loom và Airbnb. Chúng tôi gặp nhau trực tiếp hàng quý — thường là nơi có đồ ăn ngon.
          </p>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { n: "Kỹ thuật", c: "12 người" },
              { n: "Thiết kế", c: "5 người" },
              { n: "AI & Nghiên cứu", c: "4 người" },
              { n: "Tiếp thị bán hàng", c: "9 người" },
            ].map((d) => (
              <div key={d.n} className="rounded-2xl bg-card p-6">
                <div className="aspect-square w-full rounded-xl bg-muted" aria-hidden />
                <h3 className="mt-4 font-bold">{d.n}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{d.c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 py-24 md:px-6">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Được hậu thuẫn bởi</p>
              <h2 className="mt-2 font-display text-3xl leading-[1.1]">Những nhà vận hành đã từng ở đây.</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                Tự thân vận động qua năm 2023, sau đó hợp tác với các nhà vận hành và nhà đầu tư thiên thần từ Apple, Stripe, Notion, Loom và Airbnb. Chúng tôi tiết kiệm vốn, có lãi và xây dựng cho đường dài.
              </p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Báo chí nói về chúng tôi</p>
              <h2 className="mt-2 font-display text-3xl leading-[1.1]">Một dấu ấn nhỏ cho đến nay.</h2>
              <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                <li>— "Tấm danh thiếp đẹp nhất tôi từng sở hữu." <span className="text-foreground">Wired</span></li>
                <li>— "CRM của Banana là điều Salesforce lẽ ra phải làm." <span className="text-foreground">The Verge</span></li>
                <li>— "Cuối cùng, kết nối đã được tái phát minh." <span className="text-foreground">Fast Company</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 pb-24 md:px-6">
          <div className="relative overflow-hidden rounded-3xl bg-foreground px-8 py-16 text-center text-background md:p-20">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.96_0.21_117/0.15),transparent_50%)]" aria-hidden />
            <Sparkles className="mx-auto h-6 w-6 text-brand-lime" />
            <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
              Muốn xây dựng tương lai của mối quan hệ <em className="italic text-brand-lime">cùng chúng tôi?</em>
            </h2>
            <p className="mx-auto mt-5 max-w-lg text-base text-background/70">
              Chúng tôi luôn tìm kiếm nhà thiết kế, kỹ sư và nhà vận hành quan tâm đến chất lượng. Hoặc chỉ đơn giản là chào hỏi.
            </p>
            <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to="/vi/teams" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground">
                Xem vị trí tuyển dụng
              </Link>
              <Link to="/vi/contact" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold">
                Liên hệ
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}